import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Anchor, Home, Users, Activity, CheckCircle, History, ChevronDown, ChevronUp } from "lucide-react";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";
import { API } from "../App";

const getExercises = (t) => [
  {
    id: "grounding_54321",
    name: t("dissocEx.grounding.name"),
    description: t("dissocEx.grounding.desc"),
    icon: Anchor,
    color: "bg-emerald-50 text-emerald-600",
    prompts: [
      { key: "see5", label: t("dissocEx.grounding.see5"), placeholder: t("dissocEx.grounding.see5Ph") },
      { key: "hear4", label: t("dissocEx.grounding.hear4"), placeholder: t("dissocEx.grounding.hear4Ph") },
      { key: "touch3", label: t("dissocEx.grounding.touch3"), placeholder: t("dissocEx.grounding.touch3Ph") },
      { key: "smell2", label: t("dissocEx.grounding.smell2"), placeholder: t("dissocEx.grounding.smell2Ph") },
      { key: "taste1", label: t("dissocEx.grounding.taste1"), placeholder: t("dissocEx.grounding.taste1Ph") },
      { key: "orientation", label: t("dissocEx.grounding.orientation"), placeholder: t("dissocEx.grounding.orientationPh") },
      { key: "effectiveness", label: t("dissocEx.grounding.effectiveness"), placeholder: t("dissocEx.grounding.effectivenessPh") },
    ],
  },
  {
    id: "safe_place",
    name: t("dissocEx.safe.name"),
    description: t("dissocEx.safe.desc"),
    icon: Home,
    color: "bg-amber-50 text-amber-600",
    prompts: [
      { key: "visual", label: t("dissocEx.safe.visual"), placeholder: t("dissocEx.safe.visualPh") },
      { key: "sounds", label: t("dissocEx.safe.sounds"), placeholder: t("dissocEx.safe.soundsPh") },
      { key: "touch_feel", label: t("dissocEx.safe.touch"), placeholder: t("dissocEx.safe.touchPh") },
      { key: "smells", label: t("dissocEx.safe.smells"), placeholder: t("dissocEx.safe.smellsPh") },
      { key: "keyword", label: t("dissocEx.safe.keyword"), placeholder: t("dissocEx.safe.keywordPh") },
      { key: "body_sensation", label: t("dissocEx.safe.body"), placeholder: t("dissocEx.safe.bodyPh") },
    ],
  },
  {
    id: "parts_work",
    name: t("dissocEx.parts.name"),
    description: t("dissocEx.parts.desc"),
    icon: Users,
    color: "bg-violet-50 text-violet-600",
    prompts: [
      { key: "part_identified", label: t("dissocEx.parts.identified"), placeholder: t("dissocEx.parts.identifiedPh") },
      { key: "part_role", label: t("dissocEx.parts.role"), placeholder: t("dissocEx.parts.rolePh") },
      { key: "communication", label: t("dissocEx.parts.communication"), placeholder: t("dissocEx.parts.communicationPh") },
      { key: "reassurance", label: t("dissocEx.parts.reassurance"), placeholder: t("dissocEx.parts.reassurancePh") },
      { key: "new_role", label: t("dissocEx.parts.newRole"), placeholder: t("dissocEx.parts.newRolePh") },
    ],
  },
  {
    id: "window_tolerance",
    name: t("dissocEx.window.name"),
    description: t("dissocEx.window.desc"),
    icon: Activity,
    color: "bg-rose-50 text-rose-600",
    prompts: [
      { key: "current_state", label: t("dissocEx.window.state"), placeholder: t("dissocEx.window.statePh") },
      { key: "signs", label: t("dissocEx.window.signs"), placeholder: t("dissocEx.window.signsPh") },
      { key: "trigger", label: t("dissocEx.window.trigger"), placeholder: t("dissocEx.window.triggerPh") },
      { key: "strategy_used", label: t("dissocEx.window.strategy"), placeholder: t("dissocEx.window.strategyPh") },
      { key: "result", label: t("dissocEx.window.result"), placeholder: t("dissocEx.window.resultPh") },
    ],
  },
];

const DissociationExercisesPage = ({ user, logout }) => {
  const { t } = useI18n();
  const EXERCISES = getExercises(t);
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [responses, setResponses] = useState({});
  const [entries, setEntries] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/dissociation-exercises`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) { console.error(e); }
  };

  const saveSession = async () => {
    if (!selectedExercise) return;
    const filled = Object.values(responses).some(v => v.trim());
    if (!filled) { toast.error(t("exerciseForm.fillOne")); return; }
    try {
      const res = await axios.post(`${API}/dissociation-exercises`, {
        exercise_type: selectedExercise.id,
        responses,
      }, { withCredentials: true });
      toast.success(t("exerciseForm.saved"));
      if (user?.role === "patient") promptShare(res.data, "dissociation_exercises");
      setSelectedExercise(null);
      setResponses({});
      fetchEntries();
    } catch (e) { toast.error(t("exerciseForm.saveError")); }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="dissociation-exercises-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("dissocEx.pageTitle")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("dissocEx.pageSubtitle")}</p>
          </div>
          {entries.length > 0 && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <History className="w-4 h-4" /> {entries.length} {t("exerciseForm.sessions")}
            </span>
          )}
        </div>

        {selectedExercise ? (
          <Card className="max-w-2xl mx-auto" data-testid="dissociation-exercise-form">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <selectedExercise.icon className="w-5 h-5" />
                {selectedExercise.name}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{selectedExercise.description}</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {selectedExercise.prompts.map((prompt) => (
                <div key={prompt.key} className="space-y-1.5">
                  <label className="text-sm font-medium">{prompt.label}</label>
                  <Textarea
                    placeholder={prompt.placeholder}
                    value={responses[prompt.key] || ""}
                    onChange={(e) => setResponses(prev => ({ ...prev, [prompt.key]: e.target.value }))}
                    rows={3}
                    data-testid={`input-${prompt.key}`}
                  />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <Button onClick={saveSession} className="flex-1" data-testid="save-exercise-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> {t("exerciseForm.save")}
                </Button>
                <Button variant="outline" onClick={() => { setSelectedExercise(null); setResponses({}); }}>
                  {t("exerciseForm.back")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {EXERCISES.map((exercise) => (
                <Card key={exercise.id} className="card-hover cursor-pointer" onClick={() => setSelectedExercise(exercise)} data-testid={`card-${exercise.id}`}>
                  <CardContent className="p-4 sm:p-6">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${exercise.color} flex items-center justify-center mb-3 sm:mb-4`}>
                      <exercise.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <h3 className="font-heading text-base sm:text-lg font-semibold mb-1.5 sm:mb-2 break-words">{exercise.name}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground break-words">{exercise.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            {entries.length > 0 && (
              <div>
                <Button variant="ghost" className="mb-3 text-sm" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history">
                  {showHistory ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                  {t("exerciseForm.history")} ({entries.length})
                </Button>
                {showHistory && (
                  <div className="space-y-3">
                    {entries.slice(0, 10).map((entry, i) => {
                      const ex = EXERCISES.find(e => e.id === entry.exercise_type);
                      return (
                        <Card key={i} className="bg-muted/30">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium text-sm">{ex?.name || entry.exercise_type}</span>
                              <span className="text-xs text-muted-foreground">{new Date(entry.created_at).toLocaleDateString("fr-FR")}</span>
                            </div>
                            {entry.responses && Object.entries(entry.responses).slice(0, 2).map(([k, v]) => (
                              <p key={k} className="text-xs text-muted-foreground truncate">{v}</p>
                            ))}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default DissociationExercisesPage;
